import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../api/axios";
import Layout from "../components/Layout";
import toast from "react-hot-toast";
import usePlan from "../hooks/usePlan";
import { useTheme } from "../context/ThemeContext";

export default function TeamMembers() {
  const { theme } = useTheme();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", role: "staff" });
  const [sending, setSending] = useState(false);
  const { isPro } = usePlan();
  const seatsTotal = 5;

  const card = {
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: "14px",
    padding: "28px",
    marginBottom: "24px",
    boxShadow: theme.cardShadow,
  };
  const input = {
    background: theme.inputBg,
    border: `1px solid ${theme.inputBorder}`,
    borderRadius: "10px",
    padding: "12px 16px",
    color: theme.textPrimary,
    fontSize: "14px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  };

  const fetchMembers = async () => {
    try {
      const res = await API.get("/team/members");
      setMembers(res.data);
    } catch (err) {
      if (err.response?.status !== 403) toast.error("Failed to load team members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMembers(); }, []);

  const handleInvite = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await API.post("/team/invite", form);
      toast.success(`Invitation sent to ${form.email}`);
      setForm({ full_name: "", email: "", role: "staff" });
      setShowForm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invitation");
    } finally {
      setSending(false);
    }
  };

  const handleRemove = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from your team?`)) return;
    try {
      await API.delete(`/team/members/${userId}`);
      toast.success("Team member removed");
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove member");
    }
  };

  if (!isPro) {
    return (
      <Layout>
        <div style={{ marginBottom: "32px" }}>
          <h1 style={{ fontSize: "28px", fontWeight: "700", color: theme.textPrimary, letterSpacing: "-0.5px" }}>Team Members</h1>
          <p style={{ fontSize: "14px", color: theme.textSecondary, marginTop: "4px" }}>Manage your team</p>
        </div>
        <div style={{ ...card, textAlign: "center", padding: "48px 32px" }}>
          <p style={{ fontSize: "48px", marginBottom: "16px" }}>👥</p>
          <h2 style={{ fontSize: "20px", fontWeight: "700", color: theme.textPrimary, marginBottom: "12px" }}>Pro Plan Feature</h2>
          <p style={{ fontSize: "14px", color: theme.textSecondary, marginBottom: "24px", lineHeight: "1.6", maxWidth: "400px", margin: "0 auto 24px" }}>
            Team management is available on the Pro plan. Invite up to 4 additional team members to manage your inventory together.
          </p>
          <Link to="/subscription" style={{
            display: "inline-block",
            background: "linear-gradient(135deg, #2ec4b6, #1a9e92)",
            color: "#0a0f0f", borderRadius: "10px", padding: "12px 28px",
            fontSize: "14px", fontWeight: "700", textDecoration: "none",
          }}>Upgrade to Pro</Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px", flexWrap: "wrap", gap: "16px" }}>
        <div>
          <h1 style={{ fontSize: "28px", fontWeight: "700", color: theme.textPrimary, letterSpacing: "-0.5px" }}>Team Members</h1>
          <p style={{ fontSize: "14px", color: theme.textSecondary, marginTop: "4px" }}>
            {members.length} of {seatsTotal} seats used
          </p>
        </div>
        {members.length < 5 && (
          <button onClick={() => setShowForm(!showForm)} style={{
            background: "linear-gradient(135deg, #2ec4b6, #1a9e92)",
            color: "#0a0f0f", border: "none", borderRadius: "10px",
            padding: "12px 24px", fontSize: "14px", fontWeight: "700", cursor: "pointer",
          }}>
            {showForm ? "Cancel" : "+ Invite Member"}
          </button>
        )}
      </div>

      {/* Invite Form */}
      {showForm && (
        <div style={card}>
          <h2 style={{ fontSize: "18px", fontWeight: "600", color: theme.textPrimary, marginBottom: "24px" }}>
            Invite Team Member
          </h2>
          <form onSubmit={handleInvite} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <label style={{ fontSize: "13px", fontWeight: "500", color: theme.textSecondary }}>Full Name</label>
                <input type="text" value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  placeholder="Team member name" required style={input} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <label style={{ fontSize: "13px", fontWeight: "500", color: theme.textSecondary }}>Email Address</label>
                <input type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="member@email.com" required style={input} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <label style={{ fontSize: "13px", fontWeight: "500", color: theme.textSecondary }}>Role</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} style={input}>
                  <option value="manager">Manager</option>
                  <option value="staff">Staff</option>
                </select>
              </div>
            </div>
            <button type="submit" disabled={sending} style={{
              background: "linear-gradient(135deg, #2ec4b6, #1a9e92)",
              color: "#0a0f0f", border: "none", borderRadius: "10px",
              padding: "12px 32px", fontSize: "14px", fontWeight: "700",
              alignSelf: "flex-start", cursor: "pointer",
            }}>
              {sending ? "Sending..." : "Send Invitation"}
            </button>
          </form>
        </div>
      )}

      {/* Info */}
      <div style={{
        background: theme.inputBg, border: `1px solid ${theme.cardBorder}`,
        borderRadius: "10px", padding: "16px 20px", marginBottom: "24px",
      }}>
        <p style={{ fontSize: "13px", color: theme.textSecondary, lineHeight: "1.6" }}>
          💡 Team members will receive an email invitation with a link to create their account. They will automatically be linked to your business.
        </p>
      </div>

      {/* Members Table */}
      {loading ? (
        <p style={{ color: theme.textSecondary }}>Loading...</p>
      ) : (
        <div style={{
          background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
          borderRadius: "14px", overflowX: "auto",
          WebkitOverflowScrolling: "touch", boxShadow: theme.cardShadow,
        }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "600px" }}>
            <thead>
              <tr>
                {["Name", "Email", "Role", "Status", "Joined", "Action"].map((h) => (
                  <th key={h} style={{
                    textAlign: "left", padding: "14px 16px",
                    fontSize: "12px", fontWeight: "600",
                    color: theme.textSecondary, textTransform: "uppercase",
                    letterSpacing: "0.5px", borderBottom: `1px solid ${theme.divider}`,
                    background: theme.cardBg,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {members.map((member, index) => (
                <tr key={member.id} style={{ borderBottom: `1px solid ${theme.divider}` }}>
                  <td style={{ padding: "14px 16px", fontSize: "14px", color: theme.textPrimary }}>
                    {member.full_name}
                    {index === 0 && (
                      <span style={{
                        marginLeft: "8px", fontSize: "10px", fontWeight: "700",
                        background: "#162424", color: "#2ec4b6",
                        padding: "2px 8px", borderRadius: "20px",
                      }}>Owner</span>
                    )}
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: "14px", color: theme.textSecondary }}>{member.email}</td>
                  <td style={{ padding: "14px 16px" }}>
                    <span style={{
                      fontSize: "12px", fontWeight: "600", padding: "4px 10px", borderRadius: "20px",
                      background: member.role === "admin" ? "#162424" : member.role === "manager" ? "#1a2e1a" : theme.inputBg,
                      color: member.role === "admin" ? "#2ec4b6" : member.role === "manager" ? "#2ecc71" : theme.textSecondary,
                    }}>
                      {member.role}
                    </span>
                  </td>
                  <td style={{ padding: "14px 16px" }}>
                    <span style={{
                      fontSize: "12px", fontWeight: "600", padding: "4px 10px", borderRadius: "20px",
                      background: member.is_active ? "#0d2e1a" : "#2e0d0d",
                      color: member.is_active ? "#2ecc71" : "#e74c3c",
                    }}>
                      {member.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: "14px", color: theme.textSecondary }}>
                    {new Date(member.created_at).toLocaleDateString("en-NG")}
                  </td>
                  <td style={{ padding: "14px 16px" }}>
                    {index !== 0 && (
                      <button onClick={() => handleRemove(member.id, member.full_name)} style={{
                        background: "none", border: "1px solid #e74c3c",
                        borderRadius: "6px", color: "#e74c3c",
                        padding: "6px 12px", fontSize: "12px",
                        fontWeight: "600", cursor: "pointer",
                      }}>Remove</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Seat Progress */}
      <div style={{ ...card, marginTop: "24px" }}>
        <div style={{ background: theme.divider, borderRadius: "10px", height: "8px", marginBottom: "12px", overflow: "hidden" }}>
          <div style={{
            height: "100%", borderRadius: "10px",
            width: `${(members.length / 5) * 100}%`,
            background: members.length >= 5 ? "#e74c3c" : "linear-gradient(135deg, #2ec4b6, #1a9e92)",
            transition: "width 0.3s ease",
          }} />
        </div>
        <p style={{ fontSize: "13px", color: theme.textSecondary }}>{members.length} of 5 seats used</p>
      </div>
    </Layout>
  );
}